import { useState } from "react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { toast } from "sonner";
import { Plus, X, Loader2, Sparkles } from "lucide-react";
import CreditCostTag from "../../CreditCostTag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FmeaTab({ projectId, token, fmea, setFmea }) {
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const addItem = () => {
    setFmea(prev => ({
      ...prev,
      items: [...prev.items, { failure_mode: "", effect: "", cause: "", severity: 5, occurrence: 5, detection: 5, recommended_action: "", responsible: "", status: "identified" }]
    }));
  };

  const updateItem = (index, field, value) => {
    setFmea(prev => ({
      ...prev,
      items: prev.items.map((item, i) => i === index ? { ...item, [field]: value } : item)
    }));
  };

  const removeItem = (index) => {
    if (fmea.items.length <= 1) return;
    setFmea(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const aiSuggest = async () => {
    setAiLoading(true);
    try {
      const response = await fetch(`${API}/ai/suggest-fmea`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ project_id: projectId }),
      });
      if (!response.ok) throw new Error("AI FMEA generation failed");
      const data = await response.json();
      if (data.items) {
        setFmea({ process_name: data.process_name || fmea.process_name, items: data.items });
        toast.success(`${data.items.length} failure modes generated`);
      }
    } catch (error) { toast.error(error.message); }
    finally { setAiLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/projects/${projectId}/improve/fmea`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ process_name: fmea.process_name, items: fmea.items.filter(i => i.failure_mode.trim()) }),
      });
      if (!response.ok) throw new Error("Failed to save");
      const data = await response.json();
      if (data.items) setFmea(prev => ({ ...prev, items: data.items }));
      toast.success("FMEA saved");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="font-heading font-semibold">Failure Mode & Effects Analysis (FMEA)</h3>
          <p className="text-sm text-muted-foreground">RPN = Severity x Occurrence x Detection</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={aiSuggest} disabled={aiLoading} data-testid="ai-suggest-fmea-btn">
            {aiLoading ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
            {aiLoading ? "Generating..." : "AI Suggest"}
            <CreditCostTag cost={5} />
          </Button>
          <Button variant="outline" size="sm" onClick={addItem}>
            <Plus className="w-4 h-4 mr-1" /> Add Failure Mode
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <Label>Process Name</Label>
        <Input
          value={fmea.process_name}
          onChange={(e) => setFmea(prev => ({ ...prev, process_name: e.target.value }))}
          placeholder="e.g., Order Processing"
          className="max-w-md"
          data-testid="fmea-process-name"
        />
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Failure Mode</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Effect</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Cause</th>
              <th className="text-center py-2 px-2 font-medium text-muted-foreground w-16">S</th>
              <th className="text-center py-2 px-2 font-medium text-muted-foreground w-16">O</th>
              <th className="text-center py-2 px-2 font-medium text-muted-foreground w-16">D</th>
              <th className="text-center py-2 px-2 font-medium text-muted-foreground w-16">RPN</th>
              <th className="text-left py-2 px-2 font-medium text-muted-foreground">Action</th>
              <th className="w-10"></th>
            </tr>
          </thead>
          <tbody>
            {fmea.items.map((item, index) => {
              const rpn = item.severity * item.occurrence * item.detection;
              const rpnClass = rpn >= 200 ? "text-destructive font-bold" : rpn >= 100 ? "text-[hsl(var(--warning))] font-medium" : "";
              return (
                <tr key={index} className="border-b border-border">
                  <td className="py-2 px-2">
                    <Input value={item.failure_mode} onChange={(e) => updateItem(index, "failure_mode", e.target.value)} placeholder="What could fail?" className="text-sm" data-testid={`fmea-failure-${index}`} />
                  </td>
                  <td className="py-2 px-2">
                    <Input value={item.effect} onChange={(e) => updateItem(index, "effect", e.target.value)} placeholder="Effect of failure" className="text-sm" />
                  </td>
                  <td className="py-2 px-2">
                    <Input value={item.cause} onChange={(e) => updateItem(index, "cause", e.target.value)} placeholder="Root cause" className="text-sm" />
                  </td>
                  <td className="py-2 px-2">
                    <Input type="number" min="1" max="10" value={item.severity} onChange={(e) => updateItem(index, "severity", parseInt(e.target.value) || 1)} className="text-sm text-center w-14" />
                  </td>
                  <td className="py-2 px-2">
                    <Input type="number" min="1" max="10" value={item.occurrence} onChange={(e) => updateItem(index, "occurrence", parseInt(e.target.value) || 1)} className="text-sm text-center w-14" />
                  </td>
                  <td className="py-2 px-2">
                    <Input type="number" min="1" max="10" value={item.detection} onChange={(e) => updateItem(index, "detection", parseInt(e.target.value) || 1)} className="text-sm text-center w-14" />
                  </td>
                  <td className={`py-2 px-2 text-center ${rpnClass}`}>{rpn}</td>
                  <td className="py-2 px-2">
                    <Input value={item.recommended_action} onChange={(e) => updateItem(index, "recommended_action", e.target.value)} placeholder="Recommended action" className="text-sm" />
                  </td>
                  <td className="py-2 px-2">
                    {fmea.items.length > 1 && (
                      <Button variant="ghost" size="sm" onClick={() => removeItem(index)}><X className="w-3 h-3" /></Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 flex items-center gap-4 text-xs text-muted-foreground">
        <span>S = Severity (1-10)</span>
        <span>O = Occurrence (1-10)</span>
        <span>D = Detection (1-10, higher = harder to detect)</span>
        <span className="text-destructive">RPN &ge; 200: Critical</span>
        <span className="text-[hsl(var(--warning))]">RPN &ge; 100: High</span>
      </div>

      <Button onClick={save} disabled={saving} className="mt-4" data-testid="save-fmea-btn">
        {saving ? "Saving..." : "Save FMEA"}
      </Button>
    </div>
  );
}
